/**
 * Modern authentication flow component with progressive disclosure.
 *
 * Starts with email entry, detects new vs existing users, and shows
 * only relevant authentication options (password, magic link, or signup).
 *
 * Aliases: auth component, authentication flow, login signup component
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import {
  Mail,
  Lock,
  User,
  ArrowRight,
  Sparkles,
  Loader2,
  ShieldCheck,
  KeyRound,
} from "lucide-react";
import { motion } from "motion/react";
import type { FormEvent } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { AuthFormError } from "~/components/auth/auth-form-error";
import { Button } from "~/components/ui/button";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import type { AuthFlowProps } from "~/lib/auth/auth-flow-types";
import { useAuthFlow } from "~/lib/auth/use-auth-flow";
import {
  emailSchema,
  passwordSchema,
  signupCredentialsSchema,
} from "~/lib/schemas/auth-flow";

const passwordlessSignupSchema = signupCredentialsSchema.omit({
  password: true,
});

const fadeInUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.3 },
};

export function AuthFlow({
  signupMethod = "password",
  defaultEmail,
  defaultFirstName,
  onSuccess,
  onError,
  onEmailSubmitted,
  emailStepTitle = "Sign in or create an account",
  emailStepDescription = "Enter your email to get started",
  redirectUrl,
}: AuthFlowProps): React.ReactElement {
  const authFlow = useAuthFlow({
    signupMethod,
    defaultEmail,
    defaultFirstName,
    onSuccess,
    onError,
    redirectUrl,
  });

  const emailForm = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: defaultEmail ?? "" },
  });

  const passwordForm = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: "" },
  });

  const signupForm = useForm({
    resolver: zodResolver(
      signupMethod === "email_link"
        ? passwordlessSignupSchema
        : signupCredentialsSchema,
    ),
    defaultValues: {
      firstName: defaultFirstName ?? "",
      lastName: "",
      password: "",
    },
  });

  const [clientTrustCode, setClientTrustCode] = useState("");
  const [secondFactorCode, setSecondFactorCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const totpAvailable = authFlow.secondFactorStrategies.includes("totp");
  const backupAvailable =
    authFlow.secondFactorStrategies.includes("backup_code");
  const activeSecondFactorStrategy =
    backupAvailable && (useBackupCode || !totpAvailable)
      ? "backup_code"
      : "totp";
  const isUsingBackupCode = activeSecondFactorStrategy === "backup_code";
  const canSwitchSecondFactorStrategy = totpAvailable && backupAvailable;

  const handleEmailSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isValid = await emailForm.trigger();
    if (!isValid) return;

    const { email } = emailForm.getValues();
    onEmailSubmitted?.();
    try {
      await authFlow.submitEmail(email);
    } catch {
      // Error handled in hook
    }
  };

  const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isValid = await passwordForm.trigger();
    if (!isValid) return;

    const { password } = passwordForm.getValues();
    try {
      await authFlow.submitPassword(password);
    } catch {
      // Error handled in hook
    }
  };

  const handleSignupSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isValid = await signupForm.trigger();
    if (!isValid) return;

    const { firstName, lastName, password } = signupForm.getValues();
    try {
      await authFlow.submitSignup(
        firstName,
        lastName,
        signupMethod === "email_link" ? undefined : password,
      );
    } catch {
      // Error handled in hook
    }
  };

  const handleClientTrustSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = clientTrustCode.trim();
    if (trimmed.length === 0) return;
    try {
      await authFlow.submitClientTrustCode(trimmed);
      setClientTrustCode("");
    } catch {
      // Error handled in hook
    }
  };

  const handleSecondFactorSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    const trimmed = secondFactorCode.trim();
    if (trimmed.length === 0) return;
    try {
      await authFlow.submitSecondFactor(trimmed, activeSecondFactorStrategy);
      setSecondFactorCode("");
    } catch {
      // Error handled in hook
    }
  };

  // Email step
  if (authFlow.currentState === "email") {
    return (
      <motion.div {...fadeInUp} className="w-full space-y-8">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-medium">
            {emailStepTitle}
          </h2>
          <p className="text-text-secondary mt-2 text-sm">
            {emailStepDescription}
          </p>
        </div>

        <form onSubmit={handleEmailSubmit} className="space-y-2">
          <FormField
            fullWidth
            error={emailForm.formState.errors.email?.message}
          >
            <div className="relative">
              <Mail className="text-text-tertiary absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2" />
              <Input
                {...emailForm.register("email")}
                type="email"
                aria-label="Email address"
                autoComplete="email"
                placeholder="you@example.com"
                className="pl-10"
                error={emailForm.formState.errors.email?.message}
                disabled={authFlow.isProcessing}
                autoFocus
              />
            </div>
          </FormField>

          <Button
            type="submit"
            loading={authFlow.isProcessing}
            disabled={authFlow.isProcessing}
            variant="primary"
            className="w-full"
          >
            Continue
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>

          <AuthFormError message={authFlow.error} />
        </form>
      </motion.div>
    );
  }

  // Login with password
  if (authFlow.currentState === "login-password") {
    return (
      <motion.div {...fadeInUp} className="w-full space-y-8">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-medium">Welcome back!</h2>
          <p className="text-text-secondary mt-2 text-sm">
            Signing in as{" "}
            <span className="font-medium">{authFlow.formData.email}</span>
          </p>
        </div>

        {/* Magic link — primary option */}
        <div className="space-y-2">
          <Button
            type="button"
            onClick={() => authFlow.switchToMagicLink()}
            disabled={authFlow.isProcessing}
            variant="primary"
            className="w-full gap-2"
          >
            <Sparkles className="h-4 w-4" />
            Send me a magic link
          </Button>
          <p className="text-text-tertiary text-center text-xs">
            No password needed — we'll email you a one-click sign-in link.
          </p>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-text-tertiary text-xs font-medium">or</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        {/* Password */}
        <form onSubmit={handlePasswordSubmit} className="space-y-3">
          <FormField
            fullWidth
            error={passwordForm.formState.errors.password?.message}
          >
            <div className="relative">
              <Lock className="text-text-tertiary absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2" />
              <Input
                {...passwordForm.register("password")}
                type="password"
                placeholder="Enter your password"
                className="pl-10"
                error={passwordForm.formState.errors.password?.message}
                disabled={authFlow.isProcessing}
                autoFocus
              />
            </div>
          </FormField>

          <Button
            type="submit"
            loading={authFlow.isProcessing}
            disabled={authFlow.isProcessing}
            className="w-full"
          >
            Sign in with password
          </Button>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="text-text-tertiary text-xs transition-colors hover:text-brand-600"
            >
              Forgot password?
            </Link>
          </div>

          <AuthFormError message={authFlow.error} />
        </form>
      </motion.div>
    );
  }

  // Client Trust step-up — Clerk requires verifying a code on a new device
  if (authFlow.currentState === "login-client-trust") {
    const channel = authFlow.clientTrustChannel;
    const isPhone = channel?.strategy === "phone_code";
    const targetLabel = channel?.safeIdentifier ?? authFlow.formData.email;
    return (
      <motion.div {...fadeInUp} className="w-full space-y-6">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-brand-200 bg-brand-50">
            <ShieldCheck className="h-8 w-8 text-brand-600" />
          </div>
          <h2 className="font-heading text-2xl font-medium">
            Verify it's really you
          </h2>
          <p className="text-text-secondary mx-auto mt-2 max-w-md text-sm">
            We don't recognize this device. We sent a verification code to{" "}
            <span className="font-medium">{targetLabel}</span>. Enter it below
            to finish signing in.
          </p>
        </div>

        <form onSubmit={handleClientTrustSubmit} className="space-y-3">
          <FormField label="Verification code">
            <Input
              value={clientTrustCode}
              onChange={(event) => setClientTrustCode(event.target.value)}
              autoComplete="one-time-code"
              inputMode={isPhone ? "numeric" : "text"}
              pattern={isPhone ? "[0-9]*" : undefined}
              placeholder="123456"
              disabled={authFlow.isProcessing}
              autoFocus
            />
          </FormField>

          <Button
            type="submit"
            loading={authFlow.isProcessing}
            disabled={
              authFlow.isProcessing || clientTrustCode.trim().length === 0
            }
            variant="primary"
            className="w-full"
          >
            Verify and continue
          </Button>

          <div className="text-text-secondary flex items-center justify-between text-sm">
            <span>Didn't get a code?</span>
            <Button
              type="button"
              variant="link"
              className="px-0"
              disabled={authFlow.isProcessing}
              onClick={() => authFlow.resendClientTrustCode()}
            >
              Resend code
            </Button>
          </div>

          <AuthFormError message={authFlow.error} />
        </form>
      </motion.div>
    );
  }

  // Two-factor authentication — the account has MFA (TOTP / backup code) enabled
  if (authFlow.currentState === "login-second-factor") {
    return (
      <motion.div {...fadeInUp} className="w-full space-y-6">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-brand-200 bg-brand-50">
            <KeyRound className="h-8 w-8 text-brand-600" />
          </div>
          <h2 className="font-heading text-2xl font-medium">
            Two-factor authentication
          </h2>
          <p className="text-text-secondary mx-auto mt-2 max-w-md text-sm">
            {isUsingBackupCode
              ? "Enter one of your backup codes to finish signing in."
              : "Enter the code from your authenticator app to finish signing in."}
          </p>
        </div>

        <form onSubmit={handleSecondFactorSubmit} className="space-y-3">
          <FormField
            label={isUsingBackupCode ? "Backup code" : "Verification code"}
          >
            <Input
              value={secondFactorCode}
              onChange={(event) => setSecondFactorCode(event.target.value)}
              autoComplete="one-time-code"
              inputMode={isUsingBackupCode ? "text" : "numeric"}
              pattern={isUsingBackupCode ? undefined : "[0-9]*"}
              placeholder={isUsingBackupCode ? "xxxxx-xxxxx" : "123456"}
              disabled={authFlow.isProcessing}
              autoFocus
            />
          </FormField>

          <Button
            type="submit"
            loading={authFlow.isProcessing}
            disabled={
              authFlow.isProcessing || secondFactorCode.trim().length === 0
            }
            variant="primary"
            className="w-full"
          >
            Verify and continue
          </Button>

          {canSwitchSecondFactorStrategy && (
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                className="px-0 text-xs"
                disabled={authFlow.isProcessing}
                onClick={() => {
                  setUseBackupCode((prev) => !prev);
                  setSecondFactorCode("");
                }}
              >
                {useBackupCode
                  ? "Use your authenticator app instead"
                  : "Use a backup code instead"}
              </Button>
            </div>
          )}

          <AuthFormError message={authFlow.error} />
        </form>
      </motion.div>
    );
  }

  // Login/Signup with magic link (waiting for email click)
  if (
    authFlow.currentState === "login-magic" ||
    authFlow.currentState === "verify-email"
  ) {
    const isSignup = authFlow.currentState === "verify-email";

    return (
      <motion.div {...fadeInUp} className="w-full space-y-4">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-brand-200 bg-brand-50">
            <Mail className="h-8 w-8 text-brand-600" />
          </div>

          <h2 className="font-heading text-2xl font-medium">
            Check your email
          </h2>
          <p className="text-text-secondary mx-auto mt-2 max-w-md text-sm">
            We sent a magic link to{" "}
            <span className="font-medium">{authFlow.formData.email}</span>.
            Click the link to {isSignup ? "verify your account" : "sign in"}.
          </p>

          {authFlow.isProcessing && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <div className="h-2 w-2 animate-bounce rounded-full bg-brand-600 [animation-delay:-0.3s]" />
              <div className="h-2 w-2 animate-bounce rounded-full bg-brand-600 [animation-delay:-0.15s]" />
              <div className="h-2 w-2 animate-bounce rounded-full bg-brand-600" />
            </div>
          )}
        </div>

        <AuthFormError message={authFlow.error} />
        {isSignup && authFlow.error && !authFlow.isProcessing && (
          <Button
            onClick={() => {
              const { firstName, lastName, password } = authFlow.formData;
              void authFlow.submitSignup(
                firstName,
                lastName,
                signupMethod === "email_link" ? undefined : password,
              );
            }}
          >
            Try again
          </Button>
        )}
      </motion.div>
    );
  }

  // Signup credentials
  if (authFlow.currentState === "signup-credentials") {
    return (
      <motion.div {...fadeInUp} className="w-full space-y-8">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-medium">
            Create your account
          </h2>
          <p className="text-text-secondary mt-2 text-sm">
            for <span className="font-medium">{authFlow.formData.email}</span>
          </p>
        </div>

        <form onSubmit={handleSignupSubmit} className="space-y-2">
          <FormField
            fullWidth
            error={signupForm.formState.errors.firstName?.message}
          >
            <div className="relative">
              <User className="text-text-tertiary absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2" />
              <Input
                {...signupForm.register("firstName")}
                type="text"
                placeholder="First name"
                autoComplete="given-name"
                className="pl-10"
                error={signupForm.formState.errors.firstName?.message}
                disabled={authFlow.isProcessing}
                autoFocus
              />
            </div>
          </FormField>

          <FormField
            fullWidth
            error={signupForm.formState.errors.lastName?.message}
          >
            <div className="relative">
              <User className="text-text-tertiary absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2" />
              <Input
                {...signupForm.register("lastName")}
                type="text"
                placeholder="Last name"
                autoComplete="family-name"
                className="pl-10"
                error={signupForm.formState.errors.lastName?.message}
                disabled={authFlow.isProcessing}
              />
            </div>
          </FormField>

          {signupMethod === "password" && (
            <FormField
              fullWidth
              error={signupForm.formState.errors.password?.message}
            >
              <div className="relative">
                <Lock className="text-text-tertiary absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2" />
                <Input
                  {...signupForm.register("password")}
                  type="password"
                  placeholder="Create a password"
                  className="pl-10"
                  error={signupForm.formState.errors.password?.message}
                  disabled={authFlow.isProcessing}
                />
              </div>
            </FormField>
          )}

          {signupMethod === "email_link" && (
            <p className="text-sm text-gray-600">No password needed.</p>
          )}

          <div id="clerk-captcha" className="w-full" />

          <Button
            type="submit"
            loading={authFlow.isProcessing}
            disabled={authFlow.isProcessing}
            variant="primary"
            className="w-full"
          >
            Create account
          </Button>

          <AuthFormError message={authFlow.error} />
        </form>
      </motion.div>
    );
  }

  // Completed — waiting for navigation to finish
  return (
    <motion.div {...fadeInUp} className="w-full py-8 text-center">
      <Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-600" />
      <p className="text-text-secondary mt-3 text-sm">Logging you in…</p>
    </motion.div>
  );
}
